import { useEffect, useState } from "react";
import { z } from "zod";

import { useSearchWithDefault, useStorage } from "@smart/itops-hooks-dom";
import { useSmokeballApp } from "@smart/itops-smokeball-app-dom";
import { useLoadPendingSubmissions } from "@smart/manage-gql-client-dom";

import { AutomaticRemindersOffBanner } from "./automatic-reminders-off-banner";
import { FilterSync, FilterSyncContext } from "./filter-sync-context";
import { Heading } from "./heading";
import { SubmissionLists } from "./lists";
import { SubmissionWidgets } from "./submission-widgets";
import { HomeTabKey, syncWidgetFilterToTable } from "./utils";
import { VoiceGuideBanner } from "../shared";

export const HomeView = () => {
  const { tab, submissionId } = useSearchWithDefault((p) => ({
    tab: p.get("tab"),
    submissionId: p.get("submissionId"),
  }));
  const [filterSync, setFilterSync] = useState<FilterSync | null>(null);
  const [filterSyncNonce, setFilterSyncNonce] = useState(0);
  const [currentTab, setCurrentTab] = useStorage({
    defaultValue: "web-enquiries",
    key: "home-tab",
    schema: z.string(),
    storage: "local",
  });

  const { host } = useSmokeballApp();
  /**
   * Lift this to the top level as they are the same for the widget and the table.
   * And the actions in the table will need to trigger a refetch of the widget.
   */
  const pendingSubmissions = useLoadPendingSubmissions({
    reloadOnVisible: true,
    pollInterval: 180000,
  });

  useEffect(() => {
    if (tab) setCurrentTab(tab);
  }, [tab, setCurrentTab]);

  const handleWidgetClick = (tabKey: HomeTabKey) => {
    const syncedFilter = syncWidgetFilterToTable(tabKey);
    setCurrentTab(tabKey);

    const nextNonce = filterSyncNonce + 1;
    setFilterSyncNonce(nextNonce);
    setFilterSync({
      tabKey,
      period: syncedFilter.period,
      range: syncedFilter.range,
      nonce: nextNonce,
    });
  };

  return (
    <FilterSyncContext.Provider value={filterSync}>
      <div className="flex flex-col h-screen bg-neutral-50">
        <Heading
          navigate={(target: "intake-form-builder" | "intake-settings") =>
            host?.navigate({ target })
          }
        />
        <AutomaticRemindersOffBanner
          submissions={pendingSubmissions.result}
          loading={pendingSubmissions.loading}
        />
        <VoiceGuideBanner />
        <div className="px-16 py-8">
          <SubmissionWidgets
            pendingSubmissions={pendingSubmissions}
            onWidgetClick={handleWidgetClick}
          />
        </div>
        <SubmissionLists
          pendingSubmissions={pendingSubmissions}
          activeTab={currentTab}
          onTabChange={setCurrentTab}
          submissionId={submissionId || undefined}
        />
      </div>
    </FilterSyncContext.Provider>
  );
};
